import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';

export interface BookingPrerequisite {
  id: 'hours' | 'cleaner';
  title: string;
  description: string;
  href: string;
  done: boolean;
}

/**
 * Loads the two things a tenant needs before the booking page can offer slots:
 *  - at least one open day in `business_hours` (the opening hours), and
 *  - at least one team member who has availability set (an available cleaner).
 *
 * Returned as a small checklist so the admin setup prompt can list only the
 * steps that are still outstanding and link straight to the page that fixes
 * each one. Errors are swallowed (empty array) so the prompt never gets stuck
 * loading or shown on a transient failure — matching useGettingStartedChecklist.
 */
export function useBookingPrerequisites(orgId: string | null | undefined) {
  return useQuery({
    queryKey: ['booking-prerequisites', orgId],
    enabled: Boolean(orgId),
    queryFn: async (): Promise<BookingPrerequisite[]> => {
      try {
        const [hoursResult, teamResult] = await Promise.all([
          supabase
            .from('business_hours')
            .select('id', { count: 'exact', head: true })
            .eq('organization_id', orgId!)
            .eq('is_open', true),
          supabase
            .from('team_members')
            .select('id')
            .eq('organization_id', orgId!),
        ]);

        const hasHours = (hoursResult.count ?? 0) >= 1;
        const teamIds = (teamResult.data ?? []).map((row) => row.id);
        const hasTeam = teamIds.length >= 1;

        // A cleaner only counts as "available" once they have at least one
        // availability slot turned on — that is what the booking slot logic reads.
        let hasAvailability = false;
        if (hasTeam) {
          const availabilityResult = await supabase
            .from('team_availability')
            .select('id', { count: 'exact', head: true })
            .in('team_member_id', teamIds)
            .eq('is_available', true);
          hasAvailability = (availabilityResult.count ?? 0) >= 1;
        }

        return [
          {
            id: 'hours',
            title: 'Set your opening hours',
            description:
              'Booking slots are built from your opening hours — add the days and times you take bookings.',
            href: '/admin/operational-hours',
            done: hasHours,
          },
          {
            id: 'cleaner',
            title: hasTeam ? 'Set cleaner availability' : 'Add an available cleaner',
            description: hasTeam
              ? 'Your team has no availability yet — mark when your cleaners can work so slots can be offered.'
              : 'Add a cleaner and set their availability so jobs can be assigned and slots offered.',
            href: '/admin/team',
            done: hasTeam && hasAvailability,
          },
        ];
      } catch {
        // Silently hide the prompt on error rather than leaving it stuck loading.
        return [];
      }
    },
  });
}
